// Step-by-step directions container for the pathfinder — a bottom collapsible
// bar (the leading instruction + a count) that expands upward into the full
// list. Ported from Welcome Day's .wd-directions, restyled to the pathfinder
// palette. Copy-out clean: no imports outside src/pathfinder/.
//
// A step at a node carrying a photo shows a binoculars button on the right —
// the same glyph as the gold pin on the map, so the two read as one feature.
// Tapping the step (anywhere on it) goes to that node and opens the preview,
// since "look at this spot" is the whole reason to tap it. Photos on plain
// corridor nodes get a step of their own from buildDirections; where a turn or
// door already speaks for the node, that step carries the binoculars.
import { useEffect, useRef } from 'react';
import PlaceIcon from './PlaceIcons.jsx';

export default function DirectionsSteps({
  steps = [], open, onToggle, onStepClick, onStepLook, lookNodeIds, activeStepId = null, destLabel,
}) {
  // Selecting a turn icon on the map opens this list, and the step it means is
  // usually somewhere down it — scroll to it, or the selection is invisible and
  // the tap looks like it did nothing.
  const listRef = useRef(null);
  useEffect(() => {
    if (!open || activeStepId == null) return;
    const el = listRef.current?.querySelector('[data-step-active="1"]');
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [open, activeStepId]);

  if (!steps.length) return null;
  const lead = steps[0];
  const canLook = d => !!(d.nodeId && onStepLook && lookNodeIds?.has(d.nodeId));
  return (
    <div className={`pfa-steps ${open ? 'open' : ''}`}>
      {open && (
        <ol className="pfa-steps-list" ref={listRef}>
          {steps.map(d => {
            const look = canLook(d);
            const clickable = look || (!!d.at && !!onStepClick);
            const active = activeStepId != null && d.id === activeStepId;
            const handleClick = (e) => {
              // Recenter the tapped step within the scrolling list, then focus the map.
              e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' });
              if (look) onStepLook(d); else onStepClick(d);
            };
            return (
              <li
                key={d.id}
                data-step-active={active ? '1' : undefined}
                className={`pfa-steps-step${clickable ? ' clickable' : ''}${look ? ' has-look' : ''}${active ? ' active' : ''}`}
                onClick={clickable ? handleClick : undefined}
                title={look ? 'Look around at this turn' : clickable ? 'Show on map' : undefined}
              >
                <span className="pfa-steps-step-ic">{d.icon}</span>
                <span className="pfa-steps-step-tx">{d.text}</span>
                {d.chip && <span className="pfa-steps-lvl">{d.chip}</span>}
                {look && (
                  <span className="pfa-steps-look" aria-label="Look around at this turn">
                    <PlaceIcon name="look" size={16} />
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <button className="pfa-steps-bar" onClick={onToggle} aria-expanded={open}>
        <span className="pfa-steps-nav">🧭</span>
        <span className="pfa-steps-lead-col">
          {destLabel && <span className="pfa-steps-dest">To {destLabel}</span>}
          <span className="pfa-steps-lead">
            <span className="pfa-steps-lead-ic">{lead.icon}</span>
            {lead.text}
          </span>
        </span>
        {lead.chip && <span className="pfa-steps-lvl">{lead.chip}</span>}
        <span className="pfa-steps-count">{steps.length}</span>
        <span className={`pfa-steps-chev ${open ? 'up' : ''}`}>⌄</span>
      </button>
    </div>
  );
}
